import { apiFetch } from '../utils/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Notification from './Notification';
import config from '../config';
import { getResumePath, mostRecentPlayableGame, readGameVisit } from '../utils/gameVisits';
import { hasScoreDraft } from '../utils/scoreDrafts';
import { canManageGame } from '../utils/gamePermissions';
import './styles/GameListComponent.css';


function GameListComponent({ showTabs = true, initialTab = 'inProgress', highlightRecent = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [userId, setUserId] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [notification, setNotification] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(null);

  const fetchGames = useCallback(async (nextPage = 1) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    if (nextPage === 1) setGames([]);
    try {
      const token = localStorage.getItem('token');
      const query = new URLSearchParams({ view: 'summary', page: nextPage, limit: 20,
        ...(showTabs ? { status: activeTab } : {}) });
      const response = await apiFetch(`${config.apiUrl}/api/games?${query}`, {
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch games');
      }
      const data = await response.json();
      if (controller.signal.aborted) return;
      const items = Array.isArray(data) ? data : data.games;
      setGames(previous => nextPage === 1 ? items : [...new Map([...previous, ...items].map(game => [game.gameId, game])).values()]);
      setPage(nextPage);
      setHasMore(!!data.hasMore);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Error fetching games:', error);
      setNotification({ 
        message: t('errorFetchingGames'), 
        type: 'error' 
      });
    } finally { if (!controller.signal.aborted) setLoading(false); }
  }, [t, activeTab, showTabs]);

  useEffect(() => {
    fetchGames();
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
    }
    return () => requestRef.current?.abort();
  }, [fetchGames]);

 /**
   * Handles the completion of a game.
   * @param {string} gameId - The ID of the game to finish.
   */
 const handleFinishGame = useCallback(async (gameId) => {
    try {
      // First, try to submit any local scores for this game
      const draftKeys = [`localScores_${gameId}`, `localStablefordScores_${gameId}`];
      const draftSnapshot = draftKeys.map(key => localStorage.getItem(key));
      const localScores = JSON.parse(draftSnapshot[0] || '{}');
      const localStablefordScores = JSON.parse(draftSnapshot[1] || '{}');

      // Only submit if there are local scores
      if (Object.keys(localScores).length > 0) {
        const token = localStorage.getItem('token');
        const submitResponse = await apiFetch(`${config.apiUrl}/api/games/${gameId}/submit-scores`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            scores: localScores,
            stablefordScores: localStablefordScores
          })
        });

        if (!submitResponse.ok) {
          const failure = await submitResponse.json().catch(() => ({}));
          if (failure.code === 'SCORE_CALCULATION_UNAVAILABLE') throw new Error(t('scoreCalculationUnavailable'));
          throw new Error('Failed to submit local scores');
        }

        // Clear local scores after successful submission
        if (draftKeys.every((key, index) => localStorage.getItem(key) === draftSnapshot[index])) {
          draftKeys.forEach(key => localStorage.removeItem(key));
        }
        if (hasScoreDraft(gameId)) throw new Error(t('scoresChangedDuringSave'));
      }

      // Now proceed with finishing the game
      const token = localStorage.getItem('token');
      const response = await apiFetch(`${config.apiUrl}/api/games/${gameId}/finish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.code === 'SCORE_CALCULATION_UNAVAILABLE' ? t('scoreCalculationUnavailable') : errorData.error || 'Failed to finish game');
      }

      await fetchGames(); // Refresh the game list
      setNotification({ 
        message: t('gameFinishedSuccessfully'), 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error finishing game:', error);
      setNotification({ 
        message: error.message || t('errorFinishingGame'), 
        type: 'error' 
      });
    }
  }, [fetchGames, t]);

  /**
   * Formats a date string into a more readable format.
   * @param {string} dateString - The date string to format.
   * @returns {string} - The formatted date.
   */
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  /**
   * Renders a single game card.
   * @param {Object} game - The game data.
   * @returns {JSX.Element} - The rendered game card.
   */
  const renderGameCard = useCallback((game) => (
    <div key={game.gameId} className="game-card">
      <h3>{game.course.Data.Name || game.course.Name}</h3>
      <p>{t('date')}: {formatDate(game.createdAt)}</p>
      <p>{t('players')}: {game.players.length}</p>
      <p>{t('holes')}: {game.selectedHoles === 'all' ? '18' : '9'}</p>
      <p>{t('scoringType')}: {t(game.scoringType)}</p>
      <p>{t('admin')}: {game.adminName}</p>
      <p className="game-status">{t('status')}: <span className={`status-badge ${game.status === 'finished' ? 'is-finished' : 'is-playing'}`}>{t(game.status)}</span></p>
      
      <div className="game-card-buttons">
        <button 
          onClick={() => navigate(getResumePath(game, userId))}
          className="join-game-button"
        >
          {game.admin !== userId && canManageGame(game, userId) ? t('manageGame') :
            (game.status === 'finished' || (game.scoringType === 'admin' && game.admin !== userId))
            ? t('viewGame') 
            : t('continueGame')}
        </button>
        
        {game.admin === userId && game.status === 'inProgress' && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleFinishGame(game.gameId);
            }} 
            className="finish-game-button"
            title={t('finishGame')}
          >
            {t('finishGame')}
          </button>
        )}
      </div>
    </div>
  ), [navigate, userId, t, handleFinishGame]);

  const recentGame = highlightRecent ? mostRecentPlayableGame(games, userId) : null;
  const recentVisit = recentGame && readGameVisit(userId, recentGame.gameId);

  return (
    <div className="game-list-container">
      {recentGame && <section className="resume-game" aria-labelledby="resume-game-title">
        <h3 id="resume-game-title">{t('resumeRecentGame')}</h3>
        <p>{recentGame.course.Data.Name || recentGame.course.Name}</p>
        <button type="button" onClick={() => navigate(getResumePath(recentGame, userId))}>
          {recentVisit ? t('resumeAtHole', { hole: recentVisit.hole }) : t('continueGame')}
        </button>
      </section>}
      {showTabs && (
        <div className="game-tabs">
          <button 
            className={`tab-button ${activeTab === 'inProgress' ? 'active' : ''}`}
            aria-pressed={activeTab === 'inProgress'}
            onClick={() => setActiveTab('inProgress')}
          >
            {t('inProgress')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'finished' ? 'active' : ''}`}
            aria-pressed={activeTab === 'finished'}
            onClick={() => setActiveTab('finished')}
          >
            {t('finished')}
          </button>
        </div>
      )}

      <div className="game-list">
        {games
          .filter(game => !showTabs || game.status === activeTab)
          .map(renderGameCard)}
      </div>

      {loading && <p role="status">{t('loading')}</p>}
      {hasMore && <button type="button" disabled={loading} onClick={() => fetchGames(page + 1)}>{t('loadMoreGames')}</button>}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

export default GameListComponent;
